/**
 * pip-panel.js — canonical Picture-in-Picture floating-panel shell.
 *
 * window.createPipPanel(opts) → { toggle, stop, isActive, open }
 *
 * Extracted 2026-06-13 from the two near-identical copies that had grown
 * (voice-popout.js + daily-routine-popout.js) before a THIRD consumer
 * (screen-recorder) would have made it three — exactly the duplication
 * CLAUDE.md flags as a bug. The shell owns:
 *   - capability detection (Document PiP → video-PiP → unsupported)
 *   - the rAF render loop driven by opts.getState()
 *   - lifecycle (open / stop / toggle, pagehide + leavepictureinpicture)
 *   - inline-CSS injection (no FOUC), lang/dir, telemetry
 *   - optional page-button label swap
 * The CONSUMER injects only its renderers (DOM + canvas) + getState.
 *
 * Why two renderers: Document PiP (Chromium 116+) gives a real interactive
 * HTML window — buttons inside are clickable. The video-PiP fallback
 * (Firefox/Safari/older Chromium) paints a <canvas> into a hidden <video>
 * → only the system PiP chrome exists, so NO in-window button works there
 * (consumer must accept a display-only fallback or hint that the in-window
 * control needs Chromium).
 *
 * opts = {
 *   width, height,                 // requestWindow size (defaults 320×240)
 *   title,                         // PiP document <title>
 *   cssText,                       // inline CSS for the DocPiP window
 *   strings: { lang, dir, openLabel, stopLabel },
 *   button,                        // optional page-side toggle <element> (label swap)
 *   createDomRenderer: function(doc, onStop) -> { render(state) } | null,
 *   createCanvasRenderer: function(canvas) -> { render(state) },
 *   getState: function() -> state, // polled every animation frame
 *   onTelemetry: function({strategy, ok, err}){},
 *   onUnsupported: function(){},
 *   onStop: function(reason){}     // reason: 'btn'|'toggle'|'pagehide'|'leavepip'
 * }
 *
 * NOTE: requestWindow / requestPictureInPicture require a user gesture and
 * a visible opener page — call toggle()/open() from a click handler.
 */
(function (w) {
'use strict';
if (w.createPipPanel) return;

w.createPipPanel = function (opts) {
    opts = opts || {};
    var hasDocPiP = !!(window.documentPictureInPicture && typeof window.documentPictureInPicture.requestWindow === 'function');
    var hasVideoPiP = ('pictureInPictureEnabled' in document && document.pictureInPictureEnabled) ||
                      (typeof HTMLVideoElement !== 'undefined' && 'webkitSetPresentationMode' in HTMLVideoElement.prototype);
    var isWebkitPiP = !('pictureInPictureEnabled' in document) &&
                      (typeof HTMLVideoElement !== 'undefined' && 'webkitSetPresentationMode' in HTMLVideoElement.prototype);

    var strings = opts.strings || {};
    var pipActive = false;
    var pipWin = null;
    var pipVideo = null;
    var pipCanvas = null;
    var renderer = null;
    var rafId = null;
    var rafHost = null;

    function telemetry(strategy, ok, errMsg) {
        if (opts.onTelemetry) {
            try {
                opts.onTelemetry({ strategy: strategy, ok: ok ? 1 : 0, err: errMsg ? String(errMsg).substring(0, 100) : '' });
            } catch (e) {}
        }
    }

    function setButtonLabel(text) {
        if (!opts.button || !text) return;
        var span = opts.button.querySelector('span');
        if (span) span.textContent = text;
        else opts.button.textContent = text;
    }

    function startRaf(host) {
        rafHost = host;
        var renderOnce = function () {
            try { renderer.render(opts.getState()); return true; }
            catch (e) {
                // A throw here repeats every frame — log once and bail.
                telemetry('render', false, (e && e.message) || String(e));
                stop();
                return false;
            }
        };
        if (!renderOnce()) return;
        var loop = function () {
            if (!pipActive || !renderer) return;
            if (!renderOnce()) return;
            rafId = host.requestAnimationFrame(loop);
        };
        rafId = host.requestAnimationFrame(loop);
    }

    function stopRaf() {
        if (rafId && rafHost) { try { rafHost.cancelAnimationFrame(rafId); } catch (e) {} }
        rafId = null; rafHost = null;
    }

    function openDocumentPip() {
        window.documentPictureInPicture.requestWindow({
            width: opts.width || 320,
            height: opts.height || 240
        }).then(function (pw) {
            pipWin = pw;
            var doc = pw.document;
            doc.documentElement.lang = strings.lang || 'en';
            doc.documentElement.dir = strings.dir || 'ltr';

            var title = doc.createElement('title');
            title.textContent = opts.title || 'Timbrica';
            doc.head.appendChild(title);

            if (opts.cssText) {
                var style = doc.createElement('style');
                style.textContent = opts.cssText;
                doc.head.appendChild(style);
            }

            renderer = opts.createDomRenderer ? opts.createDomRenderer(doc, function () { stop('btn'); }) : null;
            if (!renderer) {
                try { pw.close(); } catch (e) {}
                pipWin = null;
                telemetry('docpip', false, 'template_missing');
                if (hasVideoPiP) openVideoPip(); else fireUnsupported();
                return;
            }

            pipActive = true;
            startRaf(pw);
            pw.addEventListener('pagehide', function () { stop('pagehide'); }, { once: true });
            setButtonLabel(strings.stopLabel);
            telemetry('docpip', true);
        }).catch(function (e) {
            telemetry('docpip', false, (e && e.message) || e);
            if (hasVideoPiP) openVideoPip();
            else fireUnsupported();
        });
    }

    function ensureVideoEl() {
        if (pipVideo) return;
        pipCanvas = document.createElement('canvas');
        pipCanvas.width = 320;
        pipCanvas.height = 180;
        pipVideo = document.createElement('video');
        pipVideo.muted = true;
        pipVideo.playsInline = true;
        pipVideo.setAttribute('aria-hidden', 'true');
        pipVideo.style.cssText = 'position:absolute;width:1px;height:1px;opacity:0;pointer-events:none;left:-9999px;top:0';
        document.body.appendChild(pipVideo);
        pipVideo.addEventListener('leavepictureinpicture', function () { stop('leavepip'); });
    }

    function openVideoPip() {
        if (!opts.createCanvasRenderer) { fireUnsupported(); return; }
        ensureVideoEl();
        renderer = opts.createCanvasRenderer(pipCanvas);
        try { renderer.render(opts.getState()); } catch (e) {}
        var stream = pipCanvas.captureStream(10);
        pipVideo.srcObject = stream;
        var onSuccess = function () {
            pipActive = true;
            startRaf(window);
            setButtonLabel(strings.stopLabel);
            telemetry('video', true);
        };
        var onError = function (e) {
            telemetry('video', false, (e && e.message) || e);
            fireUnsupported();
        };
        if (isWebkitPiP) {
            pipVideo.play().then(function () {
                pipVideo.webkitSetPresentationMode('picture-in-picture');
                onSuccess();
            }).catch(onError);
        } else {
            pipVideo.play().then(function () { return pipVideo.requestPictureInPicture(); })
                .then(onSuccess).catch(onError);
        }
    }

    function fireUnsupported() { if (opts.onUnsupported) { try { opts.onUnsupported(); } catch (e) {} } }

    function stop(reason) {
        if (!pipActive && !pipWin && (!pipVideo || !pipVideo.srcObject)) return;
        pipActive = false;
        stopRaf();
        renderer = null;
        if (pipWin) { var pw = pipWin; pipWin = null; try { pw.close(); } catch (e) {} }
        if (document.pictureInPictureElement) { document.exitPictureInPicture().catch(function () {}); }
        if (isWebkitPiP && pipVideo && pipVideo.webkitPresentationMode === 'picture-in-picture') {
            try { pipVideo.webkitSetPresentationMode('inline'); } catch (e) {}
        }
        if (pipVideo && pipVideo.srcObject) {
            pipVideo.srcObject.getTracks().forEach(function (t) { t.stop(); });
            pipVideo.srcObject = null;
        }
        setButtonLabel(strings.openLabel);
        if (opts.onStop) { try { opts.onStop(reason); } catch (e) {} }
    }

    function open() {
        if (pipActive) return;
        if (hasDocPiP) { openDocumentPip(); return; }
        if (hasVideoPiP) { openVideoPip(); return; }
        telemetry('none', false, 'no_pip_support');
        fireUnsupported();
    }

    function toggle() {
        if (pipActive) { stop('toggle'); return; }
        open();
    }

    return {
        toggle: toggle,
        open: open,
        stop: stop,
        isActive: function () { return pipActive; },
        capabilities: function () { return { docPiP: hasDocPiP, videoPiP: hasVideoPiP }; }
    };
};
})(window);
